import {
  ALL_FACES,
  BOARD_SIZE,
  boardCtx,
  checkEpitaphReplay,
  enemyBackRow,
  faceCode,
  forwardSign,
  generateMoves,
  homeRow,
  onBoardPawns,
  opponent,
  patternOf,
  topFace,
  type Epitaph,
  type Inscription,
  type MatchState,
  type MatchView,
  type Side,
} from '../engine';
import { unseenCards } from './determinize';
import type { Personality, SearchSettings } from './personality';

/** Composition of a set of unseen cards: count per face code, plus 'W' for Wilds. */
export interface Pool {
  readonly size: number;
  readonly counts: ReadonlyMap<string, number>;
}

export function poolOf(v: MatchView, side: Side): Pool {
  const counts = new Map<string, number>();
  const ids = unseenCards(v, side);
  for (const id of ids) {
    // Card ids are "<side>-<face>-<copy>" (§3.4); the face code is the middle part.
    const code = id.split('-')[1] ?? '?';
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  return { size: ids.length, counts };
}

/** P(a hand of h cards drawn from n contains at least one of k specific cards). */
export function probAtLeastOne(n: number, k: number, h: number): number {
  if (k <= 0 || h <= 0 || n <= 0) return 0;
  if (h >= n || k >= n) return 1;
  let none = 1;
  for (let i = 0; i < h; i++) {
    none *= (n - k - i) / (n - i);
    if (none <= 0) return 1;
  }
  return 1 - none;
}

/** Everything the evaluation needs about the decision being made. */
export interface EvalContext {
  readonly me: Side;
  readonly opp: Side;
  readonly personality: Personality;
  readonly settings: SearchSettings;
  /** The opponent's unseen cards (hand and deck). */
  readonly oppPool: Pool;
  /** Cards the opponent is expected to hold when it next acts. */
  readonly oppHand: number;
  /** Points one card is worth at this stage of the match. */
  readonly cardValue: number;
  /** Observed points per card so far (both sides). */
  readonly rate: number;
}

const feasibilityCache = new WeakMap<Inscription, Record<string, number>>();

/**
 * Fraction of board squares from which a recorded route stays on the board and respects the
 * direction rule for an uncrowned pawn of `owner`. Yellow hops are discounted because they need
 * pieces to jump.
 */
export function routeFeasibility(owner: Side, ins: Inscription): number {
  const cached = feasibilityCache.get(ins)?.[owner];
  if (cached !== undefined) return cached;
  const f = forwardSign(owner);
  const crownY = enemyBackRow(owner);
  let ok = 0;
  let total = 0;
  for (let y = 0; y < BOARD_SIZE; y++)
    for (let x = 0; x < BOARD_SIZE; x++) {
      if (y === crownY) continue;
      const weight = y === homeRow(owner) ? 2 : 1;
      total += weight;
      let px = x;
      let py = y;
      let crowned = false;
      let feasible = true;
      for (const link of ins.links) {
        const m = link.move;
        if (m.type === 'hops') {
          for (const d of m.dirs) {
            if (!crowned && d.dy * f <= 0) feasible = false;
            px += 2 * d.dx;
            py += 2 * d.dy;
            if (px < 0 || py < 0 || px >= BOARD_SIZE || py >= BOARD_SIZE) feasible = false;
          }
        } else if (m.type === 'knights') {
          for (const j of m.jumps) {
            if (!crowned && j.dy * f < 0) feasible = false;
            px += j.dx;
            py += j.dy;
            if (px < 0 || py < 0 || px >= BOARD_SIZE || py >= BOARD_SIZE) feasible = false;
          }
        } else if (m.type !== 'still') {
          if (!crowned && m.dy * f < 0) feasible = false;
          px += m.dx;
          py += m.dy;
          if (px < 0 || py < 0 || px >= BOARD_SIZE || py >= BOARD_SIZE) feasible = false;
        }
        if (!feasible) break;
        if (py === crownY) crowned = true;
      }
      if (feasible) ok += weight;
    }
  let hops = 0;
  for (const l of ins.links) if (l.move.type === 'hops') hops += l.move.dirs.length;
  const value = (ok / total) * 0.55 ** hops;
  const entry = feasibilityCache.get(ins) ?? {};
  entry[owner] = value;
  feasibilityCache.set(ins, entry);
  return value;
}

/** Estimated value, in points, of holding an Epitaph with this route (a free chain, §10.4). */
export function epitaphValue(s: MatchState, owner: Side, ins: Inscription): number {
  const c = s.config.constants;
  const faces = ins.links.map((l) => l.face);
  const { bonus } = patternOf(faces, c);
  let landings = 0;
  for (const l of ins.links) landings += l.move.type === 'hops' ? l.move.dirs.length : 1;
  const quiet = landings * c.POINTS_LANDING * (ins.links.length + bonus);
  let live = 0;
  if (s.phase !== 'MATCH_OVER') {
    const probe: Epitaph = { id: '~probe', owner, sourcePawnId: '', inscription: ins };
    for (const p of onBoardPawns(s.pawns, owner)) {
      const r = checkEpitaphReplay(s, owner, probe, p.id);
      if (r.ok && r.breakdown.score > live) live = r.breakdown.score;
    }
  }
  const feas = routeFeasibility(owner, ins);
  return 0.4 * live + quiet * (0.35 + 0.65 * feas) + 1.5;
}

/** Value of the best Epitaph a side holds or is about to choose from. */
export function heldEpitaphValue(s: MatchState, side: Side): number {
  let best = 0;
  const pool =
    s.pendingEpitaphChoice && s.pendingEpitaphChoice.owner === side
      ? s.pendingEpitaphChoice.candidates
      : s.players[side].epitaphs;
  for (const e of pool) best = Math.max(best, epitaphValue(s, side, e.inscription));
  return best;
}

/** What losing a pawn costs its owner: the opponent's points and tempo, minus the Epitaph gained. */
function pawnLossValue(s: MatchState, pawnId: string, ctx: EvalContext): number {
  const p = s.pawns[pawnId];
  if (!p) return 0;
  const c = s.config.constants;
  let loss = c.POINTS_CAPTURE * 1.6 + (p.crowned ? 3 : 1.2);
  if (p.inscription && ctx.settings.tactical) {
    const held = s.players[p.side].epitaphs.length > 0;
    loss -=
      ctx.personality.epitaphRegard * epitaphValue(s, p.side, p.inscription) * (held ? 0.35 : 0.8);
  }
  return Math.max(0.5, loss);
}

/**
 * Expected loss from enemy captures on the opponent's next turn, given the top left behind and
 * the probability that the opponent holds a matching card (§20 baseline heuristic).
 */
export function threatLoss(s: MatchState, ctx: EvalContext): number {
  const { me, opp } = ctx;
  if (s.phase === 'MATCH_OVER' || s.players[opp].passed) return 0;
  const mine = onBoardPawns(s.pawns, me);
  const theirs = onBoardPawns(s.pawns, opp);
  if (mine.length === 0 || theirs.length === 0) return 0;
  const top = topFace(s);
  const cbx = boardCtx(s);
  const n = ctx.oppPool.size;
  const h = ctx.oppHand;
  const pWild =
    theirs.length >= 2 ? 0.75 * probAtLeastOne(n, ctx.oppPool.counts.get('W') ?? 0, h) : 0;
  const risk = new Map<string, number>();
  for (const face of ALL_FACES) {
    const matches = !top || face.color === top.color || face.number === top.number;
    const pFace = matches ? probAtLeastOne(n, ctx.oppPool.counts.get(faceCode(face)) ?? 0, h) : 0;
    const p = 1 - (1 - pFace) * (1 - pWild);
    if (p < 0.01) continue;
    for (const op of theirs)
      for (const mv of generateMoves(cbx, op, face))
        for (const cap of mv.captures) if ((risk.get(cap) ?? 0) < p) risk.set(cap, p);
  }
  const losses = [...risk.entries()]
    .map(([id, p]) => p * pawnLossValue(s, id, ctx))
    .sort((a, b) => b - a);
  return losses.reduce((acc, v, i) => acc + v * (i === 0 ? 1 : i === 1 ? 0.45 : 0.2), 0);
}

/** Board presence, crowned pawns and progress toward crowning. */
export function positional(s: MatchState, side: Side): number {
  let v = 0;
  const f = forwardSign(side);
  for (const p of onBoardPawns(s.pawns, side)) {
    v += 1.4;
    if (p.crowned) v += 2.5;
    else if (p.square) {
      const adv = (p.square.y - homeRow(side)) * f;
      v += adv * 0.25 + (adv === BOARD_SIZE - 2 ? 1.2 : 0);
    }
  }
  return v;
}

/** Bonus for leaving a top the opponent is unlikely to match (it must Recover or replay an Epitaph). */
export function topControl(s: MatchState, ctx: EvalContext): number {
  const top = topFace(s);
  if (!top || s.players[ctx.opp].passed) return 0;
  let matching = ctx.oppPool.counts.get('W') ?? 0;
  for (const face of ALL_FACES)
    if (face.color === top.color || face.number === top.number)
      matching += ctx.oppPool.counts.get(faceCode(face)) ?? 0;
  return 4 * (1 - probAtLeastOne(ctx.oppPool.size, matching, ctx.oppHand));
}

/** Observed points per card over the match so far, from public information. */
export function observedRate(v: MatchView): number {
  let points = 0;
  let cards = 0;
  for (const side of ['S', 'N'] as const) {
    const p = v.players[side];
    points += p.totalScore + p.roundScore;
    cards += p.spent.length + p.removed.length;
  }
  points -= v.config.northHeadStart * (v.rounds.length + 1);
  cards += v.pile.filter((e) => e.source === 'card').length;
  const prior = 4.5;
  return (Math.max(0, points) + prior * 6) / (cards + 6);
}

/** Points one card is worth now: cards matter more with more rounds left and when scarce. */
export function cardValueFor(v: MatchView, me: Side, personality: Personality): number {
  const c = v.config.constants;
  const opp = opponent(me);
  const roundsAfter = Math.max(0, c.MAX_ROUNDS - v.round);
  // The match may end this round if either side is one win away.
  const mayEnd =
    v.players[me].roundWins === c.ROUND_WINS_NEEDED - 1 ||
    v.players[opp].roundWins === c.ROUND_WINS_NEEDED - 1;
  const base = roundsAfter >= 2 ? 2.6 : roundsAfter === 1 ? (mayEnd ? 1.5 : 2.0) : 0.7;
  const mine = (v.players[me].hand?.length ?? v.players[me].handCount) + v.players[me].deckCount;
  const theirs = v.players[opp].handCount + v.players[opp].deckCount;
  const scarcity = Math.min(1.4, Math.max(0.75, 1 + (theirs - mine) / 20));
  return base * scarcity * personality.thrift;
}
